import classNames from 'classnames';
import { useCallback, useState } from 'react';
import Typography from '@alltrails/core/components/Typography';
import TextInput from '@alltrails/denali/components/TextInput';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { useExperiments } from '@alltrails/experiments';
import { clearDebugPinOverrides, updateDebugPinOverrides } from '@alltrails/maps/redux/reducer';
import type { DebugPinOverrides, ZoomBucket } from '@alltrails/maps/redux/reducer';
import pinZoomPopularityMap, {
  DEFAULT_DESKTOP_COLLAPSED_PINS,
  DEFAULT_DESKTOP_REGULAR_PINS,
  DEFAULT_DESKTOP_REGULAR_PINS_TEST,
  DEFAULT_MOBILE_COLLAPSED_PINS,
  DEFAULT_MOBILE_REGULAR_PINS,
  DEFAULT_MOBILE_REGULAR_PINS_TEST,
  ZOOM_BUCKETS
} from '@alltrails/maps/utils/pinZoomPopularityMap';
import { Modal } from '@alltrails/modal';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import styles from './UnclusteringDebugInfo.module.scss';

type LocalOverrides = Record<
  ZoomBucket,
  {
    desktopRegular: string;
    mobileRegular: string;
    desktopCollapsed: string;
    mobileCollapsed: string;
  }
>;

const getInitialLocalOverrides = (debugPinOverrides?: DebugPinOverrides, hasNewPinsExperiment?: boolean): LocalOverrides => {
  const overrides = {} as LocalOverrides;
  for (const bucket of ZOOM_BUCKETS) {
    const existing = debugPinOverrides?.[bucket];
    overrides[bucket] = {
      desktopRegular: String(
        existing?.desktopRegular ?? (hasNewPinsExperiment ? DEFAULT_DESKTOP_REGULAR_PINS_TEST[bucket] : DEFAULT_DESKTOP_REGULAR_PINS[bucket])
      ),
      mobileRegular: String(
        existing?.mobileRegular ?? (hasNewPinsExperiment ? DEFAULT_MOBILE_REGULAR_PINS_TEST[bucket] : DEFAULT_MOBILE_REGULAR_PINS[bucket])
      ),
      desktopCollapsed: String(existing?.desktopCollapsed ?? DEFAULT_DESKTOP_COLLAPSED_PINS[bucket]),
      mobileCollapsed: String(existing?.mobileCollapsed ?? DEFAULT_MOBILE_COLLAPSED_PINS[bucket])
    };
  }
  return overrides;
};

const UnclusteringDebugInfo = () => {
  const hasNewPinsExperiment = useExperiments()?.['web-discover-new-pin-explore-rm']?.value === 'on';
  const dispatch = useAppDispatch();
  const { mapZoomBucket, debugPinOverrides } = useAppSelector(state => ({
    mapZoomBucket: state.map.mapZoomBucket,
    debugPinOverrides: state.map.debugPinOverrides
  }));
  const isMobile = useIsMobileSizedScreen();
  const { maxDesktopRegularPins, maxMobileRegularPins, maxTotalDesktopPins, maxTotalMobilePins } = pinZoomPopularityMap(
    mapZoomBucket,
    debugPinOverrides,
    hasNewPinsExperiment
  );
  const maxRegularPins = isMobile ? maxMobileRegularPins : maxDesktopRegularPins;
  const maxTotalPins = isMobile ? maxTotalMobilePins : maxTotalDesktopPins;
  const maxCollapsedPins = maxTotalPins ? maxTotalPins - (maxRegularPins ?? 0) : 0;
  const [isDebugModalOpen, setIsDebugModalOpen] = useState(false);
  const [localOverrides, setLocalOverrides] = useState<LocalOverrides>(() => getInitialLocalOverrides(debugPinOverrides, hasNewPinsExperiment));

  const handleOpenModal = useCallback(() => {
    setLocalOverrides(getInitialLocalOverrides(debugPinOverrides, hasNewPinsExperiment));
    setIsDebugModalOpen(true);
  }, [debugPinOverrides, hasNewPinsExperiment]);

  const handleInputChange = useCallback((bucket: ZoomBucket, field: keyof LocalOverrides[ZoomBucket], value: string) => {
    setLocalOverrides(prev => ({
      ...prev,
      [bucket]: {
        ...prev[bucket],
        [field]: value
      }
    }));
  }, []);

  const handleApply = useCallback(() => {
    const overrides: DebugPinOverrides = {};
    for (const bucket of ZOOM_BUCKETS) {
      const local = localOverrides[bucket];
      overrides[bucket] = {
        desktopRegular: parseInt(local.desktopRegular, 10) || 0,
        mobileRegular: parseInt(local.mobileRegular, 10) || 0,
        desktopCollapsed: parseInt(local.desktopCollapsed, 10) || 0,
        mobileCollapsed: parseInt(local.mobileCollapsed, 10) || 0
      };
    }
    dispatch(updateDebugPinOverrides(overrides));
    setIsDebugModalOpen(false);
  }, [localOverrides, dispatch]);

  const handleReset = useCallback(() => {
    dispatch(clearDebugPinOverrides());
    setLocalOverrides(getInitialLocalOverrides(undefined, hasNewPinsExperiment));
  }, [dispatch, hasNewPinsExperiment]);

  const getCurrentZoomBucket = (): ZoomBucket => {
    if (mapZoomBucket && mapZoomBucket <= 4) return 4;
    if (mapZoomBucket && mapZoomBucket <= 6) return 6;
    if (mapZoomBucket && mapZoomBucket <= 8) return 8;
    if (mapZoomBucket && mapZoomBucket <= 12) return 12;
    if (mapZoomBucket && mapZoomBucket <= 14) return 14;
    return 23;
  };

  const currentBucket = getCurrentZoomBucket();

  const titleMap = {
    4: 'World (Zoom 0-4)',
    6: 'Country (5-6)',
    8: 'State (7-8)',
    12: 'Area (9-12)',
    14: 'Trail (13-14)',
    23: 'Trail (15+)'
  };

  const buttons = {
    primaryButton: {
      text: 'Save',
      onClick: handleApply,
      testId: 'debug-pin-save'
    },
    flatButton: {
      text: 'Reset',
      onClick: handleReset,
      testId: 'debug-pin-reset'
    }
  };

  return (
    <>
      <button className={styles.container} onClick={handleOpenModal}>
        <Typography variant="text100" component="div">
          Pins: {maxRegularPins && maxRegularPins > 0 ? maxRegularPins : 'All'} / {maxCollapsedPins}
        </Typography>
      </button>
      {isDebugModalOpen && (
        <Modal isOpen={isDebugModalOpen} onCloseRequest={() => setIsDebugModalOpen(false)} title="Adjust pins" buttons={buttons}>
          <div>
            {ZOOM_BUCKETS.map((bucket, index) => (
              <div key={bucket} className={styles.zoomBucketSection}>
                <div className={classNames(styles.zoomBucketHeader, { [styles.currentZoomIndicator]: bucket === currentBucket })}>
                  <Typography variant="heading300" component="span">
                    {titleMap[bucket]}
                  </Typography>
                  {bucket === currentBucket && (
                    <Typography variant="text100" component="span" className={styles.currentZoomIndicatorText}>
                      Current
                    </Typography>
                  )}
                </div>
                <div className={styles.inputGrid}>
                  <TextInput
                    className={styles.textInput}
                    labelText="Default"
                    value={localOverrides[bucket].desktopRegular}
                    onChange={value => handleInputChange(bucket, 'desktopRegular', value)}
                    type="number"
                    size="sm"
                    testId={`debug-pin-desktop-regular-${bucket}`}
                    isFocused={bucket === currentBucket}
                  />
                  <TextInput
                    className={styles.textInput}
                    labelText="Collapsed"
                    value={localOverrides[bucket].desktopCollapsed}
                    onChange={value => handleInputChange(bucket, 'desktopCollapsed', value)}
                    type="number"
                    size="sm"
                    testId={`debug-pin-desktop-collapsed-${bucket}`}
                  />
                </div>
                <div className={classNames(styles.inputGrid, styles.inputGridMobile)}>
                  <TextInput
                    className={styles.textInput}
                    labelText="Mobile Default"
                    value={localOverrides[bucket].mobileRegular}
                    onChange={value => handleInputChange(bucket, 'mobileRegular', value)}
                    type="number"
                    size="sm"
                    testId={`debug-pin-mobile-regular-${bucket}`}
                    isFocused={bucket === currentBucket}
                  />
                  <TextInput
                    className={styles.textInput}
                    labelText="Mobile Collapsed"
                    value={localOverrides[bucket].mobileCollapsed}
                    onChange={value => handleInputChange(bucket, 'mobileCollapsed', value)}
                    type="number"
                    size="sm"
                    testId={`debug-pin-mobile-collapsed-${bucket}`}
                  />
                </div>
                {index < ZOOM_BUCKETS.length - 1 && <div className={styles.divider} />}
              </div>
            ))}
          </div>
        </Modal>
      )}
    </>
  );
};

export default UnclusteringDebugInfo;
